import React, { useEffect, useMemo, useState } from 'react'
import {
    downloadBlob,
    fetchChatAttachment,
    getChatAttachmentName,
    getChatAttachmentType,
    hasSecureChatAttachment,
} from '../../utils/chatAttachmentStore'

const getAccessErrorText = (error) => {
    const status = Number(error?.response?.status || 0)

    if (status === 401 || status === 403) return 'Нет доступа к этому вложению'
    if (status === 404) return 'Вложение удалено или недоступно'

    return 'Не удалось загрузить вложение'
}

const formatBytes = (value) => {
    const bytes = Number(value || 0)
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} Б`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
    return `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} МБ`
}

export default function SecureMessageAttachment({
    message,
    guestToken = '',
    onOpen,
}) {
    const [url, setUrl] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [downloading, setDownloading] = useState(false)
    const [videoRequested, setVideoRequested] = useState(false)
    const [retryNonce, setRetryNonce] = useState(0)

    const type = getChatAttachmentType(message)
    const name = getChatAttachmentName(message)
    const messageId = String(message?._id || message?.id || '')
    const fileSize = Number(message?.fileSize || message?.secureAttachment?.sizeBytes || 0)
    const meta = [formatBytes(fileSize), name].filter(Boolean).join(' · ')
    const shouldPreload = type === 'image' || type === 'voice' || (type === 'video' && videoRequested)

    useEffect(() => {
        if (!hasSecureChatAttachment(message) || !shouldPreload || !messageId) {
            setUrl('')
            setLoading(false)
            setError('')
            return undefined
        }

        let cancelled = false
        let objectUrl = ''

        setLoading(true)
        setError('')
        setUrl('')

        fetchChatAttachment({ message, guestToken })
            .then((asset) => {
                if (cancelled || !asset?.blob) return

                objectUrl = URL.createObjectURL(asset.blob)
                setUrl(objectUrl)
            })
            .catch((loadError) => {
                if (cancelled) return
                setError(getAccessErrorText(loadError))
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => {
            cancelled = true
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [guestToken, messageId, shouldPreload, type, retryNonce])

    useEffect(() => {
        setVideoRequested(false)
    }, [messageId])

    const download = async () => {
        if (downloading || !messageId) return

        setDownloading(true)
        setError('')

        try {
            const asset = await fetchChatAttachment({
                message,
                guestToken,
                download: true,
                useCache: false,
            })

            downloadBlob({
                blob: asset.blob,
                filename: asset.filename || name,
            })
        } catch (downloadError) {
            setError(getAccessErrorText(downloadError))
        } finally {
            setDownloading(false)
        }
    }

    const locked = useMemo(() => (
        error ? `🔒 ${error}` : ''
    ), [error])

    if (!hasSecureChatAttachment(message)) return null

    if (locked) {
        return (
            <div className="attachment-AO attachment-AO--locked" role="status">
                <span className="attachment-AO__lock">🔒</span>
                <span>{error}</span>
                <button type="button" onClick={() => {
                    setError('')
                    if (type === 'video') setVideoRequested(true)
                    setRetryNonce((value) => value + 1)
                }}>
                    Повторить
                </button>
            </div>
        )
    }

    if (type === 'image') {
        return (
            <button
                type="button"
                className="attachment-AO attachment-AO--image"
                onClick={() => onOpen?.(message)}
                disabled={loading || !url}
                title={name}
            >
                {url ? (
                    <img src={url} alt={name} loading="lazy" decoding="async" />
                ) : (
                    <span className="attachment-AO__skeleton" aria-label="Загружаем фото">
                        <i />
                        <b>Загружаем фото…</b>
                    </span>
                )}
            </button>
        )
    }

    if (type === 'video') {
        if (!videoRequested) {
            return (
                <button
                    type="button"
                    className="attachment-AO attachment-AO--video attachment-AO--loadable"
                    onClick={() => setVideoRequested(true)}
                    title="Загрузить видео"
                >
                    <span className="attachment-AO__play">▶</span>
                    <span className="attachment-AO__loadable-text">
                        <b>Загрузить видео</b>
                        <small>{meta || 'Защищённое видео'}</small>
                    </span>
                </button>
            )
        }

        return (
            <div className="attachment-AO attachment-AO--video">
                {url ? (
                    <video src={url} controls preload="metadata" playsInline />
                ) : (
                    <span className="attachment-AO__skeleton attachment-AO__skeleton--compact">
                        <i />
                        <b>{loading ? 'Загружаем видео…' : name}</b>
                    </span>
                )}
            </div>
        )
    }

    if (type === 'voice') {
        return (
            <div className="attachment-AO attachment-AO--voice">
                <div className="attachment-AO__voice-title">
                    <span>◉</span>
                    <div>
                        <b>Голосовое сообщение</b>
                        {!!formatBytes(fileSize) && <small>{formatBytes(fileSize)}</small>}
                    </div>
                </div>
                {url ? (
                    <audio src={url} controls preload="metadata" />
                ) : (
                    <span className="attachment-AO__audio-loading">
                        <i className="attachment-AO__spinner" />
                        {loading ? 'Загружаем аудио…' : name}
                    </span>
                )}
            </div>
        )
    }

    return (
        <button
            type="button"
            className="attachment-AO attachment-AO--doc"
            onClick={download}
            disabled={downloading}
            title="Скачать защищённое вложение"
        >
            <span className="attachment-AO__doc-icon">▤</span>
            <span className="attachment-AO__doc-main">
                <b>{name}</b>
                <small>{downloading ? 'Скачиваем…' : (formatBytes(fileSize) || 'Нажмите, чтобы скачать')}</small>
            </span>
            {downloading ? <i className="attachment-AO__spinner" /> : <span className="attachment-AO__download">↓</span>}
        </button>
    )
}
